/**
 * Church page: about church — content from churchInfo.
 */

import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Layout } from "../../components/layout/index.ts";
import { getTelegram } from "../../lib/telegram.ts";
import {
  IconMapPin,
  IconMessage,
  IconEnvelope,
  IconExternalLink,
  IconChevronRight,
} from "../../components/Icons.tsx";
import { ErrorDisplay } from "../../components/ErrorDisplay.tsx";
import { sanitizeUrl, sanitizePhone, sanitizeEmail } from "../../lib/url-utils.ts";
import { useChurchInfo } from "./hooks";

export function ChurchPage() {
  const navigate = useNavigate();
  const tg = getTelegram();
  const { info, isLoading, error, refetch } = useChurchInfo();

  // BackButton (Mini App / PWA: назад)
  useEffect(() => {
    if (!tg) return;
    tg.BackButton.show();
    const handleBack = () => {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate("/");
      }
    };
    tg.BackButton.onClick(handleBack);
    return () => {
      tg.BackButton.offClick(handleBack);
      tg.BackButton.hide();
    };
  }, [tg, navigate]);

  if (error) {
    return (
      <Layout>
        <ErrorDisplay
          error={error}
          onRetry={() => refetch({ requestPolicy: "network-only" })}
          className="border border-slate-100 ring-1 ring-slate-200/50"
        />
      </Layout>
    );
  }

  if (isLoading) {
    return (
      <Layout>
        <div className="rounded-2xl overflow-hidden bg-white/95 shadow-sm border border-slate-100 ring-1 ring-slate-200/50 p-8">
          <div className="animate-pulse space-y-4">
            <div className="h-6 bg-slate-200 rounded w-1/3" />
            <div className="h-4 bg-slate-100 rounded w-full" />
            <div className="h-4 bg-slate-100 rounded w-5/6" />
            <div className="h-4 bg-slate-100 rounded w-4/6" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!info) {
    return (
      <Layout>
        <div className="rounded-2xl overflow-hidden bg-white/95 shadow-sm border border-slate-100 ring-1 ring-slate-200/50 p-8 text-center text-slate-500 text-sm">
          Информация пока не добавлена.
        </div>
      </Layout>
    );
  }

  const title = info.title?.trim() || "О церкви";
  const heroImageUrl = (() => {
    const url = info.imageUrl?.trim();
    if (!url) return null;
    if (url.startsWith("http")) return url;
    return url.startsWith("/") ? url : `/${url}`;
  })();

  return (
    <Layout>
      {/* Hero image — как в HomeGroupsPage */}
      {heroImageUrl ? (
        <div className="aspect-[4/3] overflow-hidden rounded-2xl mb-4 shadow-sm border border-slate-100 ring-1 ring-slate-200/50 bg-slate-100">
          <img
            src={heroImageUrl}
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
      ) : null}

      <div className="rounded-2xl overflow-hidden bg-white/95 shadow-sm border border-slate-100 ring-1 ring-slate-200/50 divide-y divide-slate-100">
        <div className="p-6 xs:p-8">
          <h1 className="text-xl xs:text-2xl font-bold text-slate-900 mb-4">
            {title}
          </h1>
          {info.welcomeText?.trim() && (
            <div className="prose prose-slate prose-sm max-w-none text-slate-700 whitespace-pre-wrap">
              {info.welcomeText}
            </div>
          )}
        </div>

        {(info.address?.trim() ||
          info.mapUrl?.trim() ||
          info.parkingText?.trim()) && (
          <div className="p-6 xs:p-8">
            <h2 className="section-title px-0 text-slate-600">Как нас найти</h2>
            {info.address?.trim() && (
              <p className="text-slate-700 text-sm flex items-start gap-2">
                <IconMapPin className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                <span>{info.address}</span>
              </p>
            )}
            {info.mapUrl?.trim() && sanitizeUrl(info.mapUrl) && (
              <a
                href={sanitizeUrl(info.mapUrl)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 mt-2 text-sm text-[var(--color-accent)] font-medium"
              >
                <IconExternalLink className="w-4 h-4" />
                Открыть на карте
              </a>
            )}
            {info.parkingText?.trim() && (
              <p className="text-slate-700 text-sm mt-3 whitespace-pre-wrap">
                {info.parkingText}
              </p>
            )}
          </div>
        )}

        {(info.phone?.trim() || info.email?.trim()) && (
          <div className="p-6 xs:p-8">
            <h2 className="section-title px-0 text-slate-600">Контакты</h2>
            <div className="space-y-2 text-sm text-slate-700">
              {(() => {
                const safePhone = sanitizePhone(info.phone);
                return safePhone ? (
                  <p className="flex items-center gap-2">
                    <IconMessage className="w-4 h-4 text-slate-400" />
                    <a
                      href={`tel:${safePhone}`}
                      className="text-[var(--color-accent)]"
                    >
                      {info.phone}
                    </a>
                  </p>
                ) : null;
              })()}
              {(() => {
                const safeEmail = sanitizeEmail(info.email);
                return safeEmail ? (
                  <p className="flex items-center gap-2">
                    <IconEnvelope className="w-4 h-4 text-slate-400" />
                    <a
                      href={`mailto:${safeEmail}`}
                      className="text-[var(--color-accent)] break-all"
                    >
                      {info.email}
                    </a>
                  </p>
                ) : null;
              })()}
            </div>
          </div>
        )}

        {info.scheduleText?.trim() && (
          <Link
            to="/worship"
            className="block p-6 xs:p-8 active:bg-slate-50/50 transition-colors no-underline text-inherit group"
          >
            <h2 className="section-title px-0 text-slate-600">
              Когда мы встречаемся
            </h2>
            <p className="text-slate-700 text-sm whitespace-pre-wrap">
              {info.scheduleText}
            </p>
            <span className="inline-flex items-center gap-1 mt-2 text-sm font-medium text-[var(--color-accent)] group-active:text-amber-700">
              Расписание богослужений
              <IconChevronRight className="w-4 h-4" />
            </span>
          </Link>
        )}

        {(info.companyName?.trim() || info.companyDetails?.trim()) && (
          <div className="p-6 xs:p-8">
            <h2 className="section-title px-0 text-slate-600">
              Реквизиты и данные организации
            </h2>
            {info.companyName?.trim() && (
              <p className="font-medium text-slate-800 text-sm">
                {info.companyName}
              </p>
            )}
            {info.companyDetails?.trim() && (
              <p className="text-slate-700 text-sm whitespace-pre-wrap mt-2">
                {info.companyDetails}
              </p>
            )}
          </div>
        )}

        {info.usefulChats?.length > 0 && (
          <div className="p-6 xs:p-8">
            <h2 className="section-title px-0 text-slate-600">
              Полезные чаты и каналы
            </h2>
            <ul className="space-y-2">
              {info.usefulChats
                .filter((chat) => sanitizeUrl(chat.url))
                .map((chat, i) => (
                  <li key={i}>
                    <a
                      href={sanitizeUrl(chat.url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm text-[var(--color-accent)] font-medium"
                    >
                      <IconExternalLink className="w-4 h-4" />
                      {chat.label}
                    </a>
                  </li>
                ))}
            </ul>
          </div>
        )}

        {!info.welcomeText?.trim() &&
          !info.scheduleText?.trim() &&
          !info.address?.trim() &&
          !info.mapUrl?.trim() &&
          !info.parkingText?.trim() &&
          !info.phone?.trim() &&
          !info.email?.trim() &&
          !info.companyName?.trim() &&
          !info.companyDetails?.trim() &&
          (!info.usefulChats || info.usefulChats.length === 0) && (
            <div className="p-6 xs:p-8 text-center text-slate-500 text-sm">
              Информация пока не добавлена.
            </div>
          )}
      </div>
    </Layout>
  );
}
